/**
 * Land onboarding constants. Values must match the backend enums exactly
 * (src/enums/LandDocumentType.ts, src/modules/land/onboardingLandDraft.ts) — they are
 * sent as-is and validated server-side.
 */

/**
 * Document types the backend will actually read. Maps and location images are absent on
 * purpose: they carry no reliably readable field text, and the API rejects them rather
 * than bill for a vision call that returns confident nonsense.
 */
export const LAND_SOURCE_DOCUMENTS = [
  {
    value: 'registry_deed',
    label: 'Registry / sale deed',
    description: 'The title document. Long — only the relevant pages are read.'
  },
  {
    value: 'khasra_p1b2',
    label: 'Khasra P1B2 / RTC',
    description: 'The parcel and ownership table. Best source for areas.'
  },
  {
    value: 'diversion_order',
    label: 'Diversion / conversion order',
    description: 'Agricultural to non-agricultural permission.'
  },
  {
    value: 'land_use_certificate',
    label: 'Land use certificate',
    description: 'Zoning confirmation from the planning authority.'
  },
  {
    value: 'advocate_search',
    label: 'Advocate search report',
    description: 'Title search summary.'
  }
];

export const LAND_DOCUMENT_LABELS = LAND_SOURCE_DOCUMENTS.reduce(
  (accumulator, document) => ({ ...accumulator, [document.value]: document.label }),
  {
    khasra_map: 'Khasra / survey map',
    google_map_image: 'Location map image',
    additional: 'Additional document',
    manual: 'Entered by hand',
    description: 'Your description'
  }
);

/**
 * Draft canonical name → label, for the review list. Order here is the order shown.
 * The measurement-unit fields are omitted: they are applied alongside their area rather
 * than reviewed as separate lines.
 */
export const LAND_DRAFT_FIELD_LABELS = {
  land_name: 'Land name',
  state: 'State',
  district: 'District',
  tehsil: 'Tehsil / Taluk',
  locality: 'Locality',
  pincode: 'PIN code',
  total_main_area: 'Total area',
  total_sub_area: 'Total sub-area',
  registry_deed_number: 'Registry document number',
  registry_date: 'Registry date',
  sub_registrar_office: 'Sub-registrar office',
  land_use_classification: 'Land use',
  diversion_status: 'Diversion status'
};

/**
 * Draft canonical name → Add Land form field name, for the fields the form registers
 * directly. Owners and parcels are applied through their own handlers, not this map.
 */
export const LAND_DRAFT_FIELD_TO_FORM = {
  land_name: 'name',
  state: 'state',
  district: 'district',
  tehsil: 'tehsil',
  locality: 'area',
  pincode: 'pincode'
};

/**
 * The values a user must confirm one at a time rather than with a blanket "looks good".
 * A single global accept makes the stored provenance meaningless, and these four are what
 * a title dispute would turn on.
 */
export const LAND_SENSITIVE_FIELDS = [
  'khasra numbers',
  'parcel areas',
  'owner PAN',
  'registry document number'
];

/** Requirement state → how the readiness checklist renders it. */
export const LAND_REQUIREMENT_STATE_STYLES = {
  satisfied: { dot: 'bg-emerald-500', text: 'text-stone-700' },
  missing: { dot: 'bg-red-500', text: 'text-stone-900' },
  not_applicable: { dot: 'bg-stone-200', text: 'text-stone-400' }
};

export const LAND_REQUIREMENT_STATE_LABELS = {
  satisfied: 'Done',
  missing: 'Required',
  not_applicable: 'Not applicable'
};
